// External dependencies
import moment from 'moment';

// Internal dependencies
import { calculateTrendline } from '../../../../services/trendline';
import { scaleORF } from '../scaling';
import { getSchoolYearWithFallbacks } from '../../../../utilities/getSchoolYearWithFallbacks';
import store from '@/store';

function getCurrentPeriod(date, assessmentCalendar) {
  if (!assessmentCalendar?.periods?.length) {
    return null;
  }

  const targetDate = moment(date);
  
  // Find period that contains the date
  const period = assessmentCalendar.periods.find(period => {
    const periodStart = moment(period.startDate);
    const periodEnd = moment(period.endDate);
    return targetDate.isBetween(periodStart, periodEnd, 'day', '[]');
  });
  
  return period ? period.periodIdentifier : null;
}

// grade_level is an integer: [0, 1, 2, 3] for grades K-3
// Note that "grade" is a string in the graphQL schema
// and is treated as a string in trendline.js and the all_percentiles object
export async function generateProgressReportTrendline(data, scale, grade_level, locale, schoolYearEndDate = null) {

  // if there is no grade for student, then turn trendline off
  if(!grade_level && grade_level != 0) {
    return [];
  }

  if(grade_level > 6) grade_level = 6;

  // trendline will get generated no matter what. With fewer data points,
  // Bayesian prior from benchmarks will dictate slope. With more observations,
  // data have increasing influence on shape of trend line

  let n_points = data.length
  let latest_activity_date = data[n_points-1].date

  // Get assessment calendar from store
  const state = store.getState();
  const assessmentCalendar = state.calendar?.assessmentCalendar;
  
  // Get current period based on latest activity date
  let currentPeriod = getCurrentPeriod(latest_activity_date, assessmentCalendar);
  
  // Generate a list of days on which we want to see a trend.
  const firstActivityDate = moment(data[0].date);
  let trendEndDate;
  
  if (schoolYearEndDate) {
    // Use actual school year end date but subtract a few days for better visual positioning
    trendEndDate = moment(schoolYearEndDate).subtract(5, 'days').valueOf();
  } 
  else {
    // Get end date using fallback utility
    const schoolYearCalendar = state.calendar?.schoolYearCalendar;
    
    const schoolYearResult = getSchoolYearWithFallbacks(assessmentCalendar, schoolYearCalendar);
    trendEndDate = moment(schoolYearResult.endDate).subtract(5, 'days').valueOf();
  }

  let activityDate = firstActivityDate;
  let outputDates = [];
  let counter = 0;
  while(activityDate <= trendEndDate) {
    activityDate = moment(firstActivityDate).clone().add(counter, 'days').valueOf();
    outputDates.push(activityDate);
    counter++;
  }

  let dateScorePairs;
  let inputScale = scale;

  dateScorePairs = data.map(datapoint => [datapoint.date,datapoint.score]);

  // clean dateScorePairs of any null scores
  dateScorePairs = dateScorePairs.filter(pair => Array.isArray(pair) && pair.length > 1 && pair[1] !== null);
  // If no valid score pairs after filtering, return empty array
  if (dateScorePairs.length === 0) {
    console.warn('No valid score pairs after filtering');
    return [];
  }

  let trendline = await calculateTrendline(dateScorePairs, outputDates, inputScale, currentPeriod, grade_level, locale);

  // handle max scores for ESRI and PSF and NWF
  if(scale == 'ESRI' || scale == 'PSF' || scale == 'NWF') {
    trendline = trendline.map(point => {
      let score = point[1];
      if(score > 100) {
        score = 100;
      }
      return [point[0],score];
    });
  }
  return trendline;
}

function generateSmoothedTrendline(trendline) {
  let lastPoint = trendline.pop();
  let smoothedTrendline = [trendline[0]];
  for(let i = 1; i < trendline.length; i++) {
    if(trendline[i][1] != trendline[i - 1][1]) {
      smoothedTrendline.push(trendline[i]);
    }
  }
  smoothedTrendline.push(lastPoint);
  return smoothedTrendline;
}

export default {
  generateProgressReportTrendline,
}
